import { AttributeKey, TournamentState, utils } from '@ps/poker-games-tile-mappers';
import type { Tag } from '@ps/poker-games-tile-mappers/dist/types/poker-games-tile-mappers/src/formatters/game';
import type { PokerMttQueryVariables, Website } from '@ps/web-martech-cms-types';
import { merge } from 'lodash';
import type { Row } from 'react-table';

import type { TournamentsQueryVariables } from '@swf/generated/graphql/poker/MTT/operations';
import type { Currency, Maybe, Site } from '@swf/generated/graphql/poker/MTT/schemas';

import type {
  FilterVariables,
  NormalizedTournamentsResponse,
  Tournament,
  TournamentAttributes,
  TournamentRaw
} from '../../types/MTT';
import { formatTournamentAttr as formatTournamentAttrBase } from '../tournaments';

import { normalizeConfigQueryVariables } from './filters';

export interface CommonQueryVariableOptions {
  country?: string;
  isEmbedded: boolean;
  isPlayMoney: boolean;
  satelliteTargetRef?: string;
  siteName: Website['site'];
  tournGroupRef?: string;
  upcomingOnly?: boolean;
}

const ATTRIBUTE_KEY_MAP: Partial<{
  [K in AttributeKey]: keyof TournamentAttributes;
}> = {
  [AttributeKey.buyIn]: 'formattedBuyIn',
  [AttributeKey.prizePool]: 'formattedPrizePool',
  [AttributeKey.winnerAward]: 'formattedWinnerAward',
  [AttributeKey.shortPrizePool]: 'shortPrizePool'
};

const TOURNAMENT_STATES_ORDER: Partial<{
  [K in TournamentState]: number;
}> & { default: number } = {
  // sort all others at the bottom of the list
  default: 100,
  [TournamentState.CANCELLED]: 0,
  [TournamentState.COMPLETED]: 1,
  [TournamentState.PAUSED]: 2,
  [TournamentState.RUNNING]: 3,
  [TournamentState.LATE_REGISTRATION]: 4,
  [TournamentState.SEATING]: 5,
  [TournamentState.PHASE]: 6,
  [TournamentState.REGISTERING]: 7,
  [TournamentState.ANNOUNCED]: 8,
  [TournamentState.RESTRICTED]: 9
};

const SITE_NAME_SITE_VARIABLE_MAP: { [K in Website['site']]: Maybe<Site> } = {
  ARBA: 'BUENOS_AIRES',
  ASIA: 'ASIA',
  BE: 'BELGIUM',
  BEL: 'BELGIUM',
  BG: 'BULGARIA',
  BUL: 'BULGARIA',
  CAON: 'CA_ON',
  CA_ON: 'CA_ON',
  CH: 'SWITZERLAND',
  COM: 'COM',
  CZ: 'CZECH_REPUBLIC',
  CZE: 'CZECH_REPUBLIC',
  DE: 'GERMANY',
  DESH: 'DESH',
  DE_SH: 'DESH',
  DK: 'DENMARK',
  DEN: 'DENMARK',
  EE: 'ESTONIA',
  EST: 'ESTONIA',
  ES: 'SPAIN',
  SPA: 'SPAIN',
  EU: 'EU',
  FR: 'FRANCE',
  FRA: 'FRANCE',
  GR: 'GREECE',
  IN: 'INDIA',
  IND: 'INDIA',
  IT: 'ITALY',
  ITA: 'ITALY',
  NET: 'NET',
  NL: 'NL',
  PT: 'PORTUGAL',
  POR: 'PORTUGAL',
  RO: 'ROMANIA',
  ROM: 'ROMANIA',
  RU: 'RUSSIA',
  RUSO: 'RUSSIAN_FED_RM',
  RUSOPM: 'RUSSIAN_FED_PM',
  SE: 'SWEDEN',
  SWE: 'SWEDEN',
  UK: 'UK',
  USCO: 'US_CO',
  US_CO: 'US_CO',
  USMI: 'USMI',
  USNJ: 'NEW_JERSEY',
  US_NJ: 'NEW_JERSEY',
  USPA: 'US_PA',
  US_PA: 'US_PA',
  // missing on WMT
  SISAL: null,
  IT_SISAL_EXT: null
};

export const formatMTTournamentAttr = (
  tournament: TournamentRaw | Tournament,
  locale: string,
  isPlayMoney: boolean,
  timeZone: string
) => formatTournamentAttrBase(tournament, locale, isPlayMoney, timeZone, ATTRIBUTE_KEY_MAP);

export const formatTournament = (
  rawTournament: TournamentRaw,
  locale: string,
  timeZone: string
): Tournament => {
  const isPlayMoney = rawTournament.currency === 'PLAY';
  const formattedName = rawTournament.tournTypeName.replace(/(<([^>]+)>)/gi, '');
  const formattedSidecarName = rawTournament.name.replace(/(<([^>]+)>)/gi, '');
  const formattedSatelliteTargetName = rawTournament.satelliteTargetNameMTT.replace(
    /(<([^>]+)>)/gi,
    ''
  );
  const tags: Tag[] = utils.mttUtils.formatTags({
    game: rawTournament.gameInt,
    structure: rawTournament.structureInt,
    maxPlayers: rawTournament.maxPlayers,
    minPlayers: rawTournament.minPlayers,
    maxPerTable: rawTournament.seats,
    numReentries: rawTournament.reEntryAllowed,
    satelliteTarget: rawTournament.satelliteTarget,
    tournGroup: rawTournament.tournGroup,
    tournFlags: rawTournament.tournFlags,
    speedDisplay: rawTournament.tournSpeedInt,
    tournFlags2: String(rawTournament.tournFlags2),
    tournFlagsServInt: rawTournament.tournFlagsServInt,
    isMTT: true,
    awardScheme: rawTournament.awardSchema,
    timedTournType: rawTournament.timedTournType,
    timedTournPlayersPercent: rawTournament.timedTournPlayersPercent,
    isHiLo: rawTournament.isHiLo
  });
  const formattedGame = utils.mttUtils.formatGame(
    rawTournament.gameInt,
    rawTournament.structureInt,
    rawTournament.isHiLo,
    false
  );
  const formattedState = utils.mttUtils.getMttState(
    rawTournament.legacyStatus,
    rawTournament.isRestricted,
    rawTournament.tournFlagsServInt
  );
  const formattedAttribtues = formatMTTournamentAttr(rawTournament, locale, isPlayMoney, timeZone);
  const imageUrl = utils.mttUtils.formatImgMtt(
    rawTournament.gameInt,
    rawTournament.buyIn,
    rawTournament.structureInt,
    Number(rawTournament.isHiLo),
    rawTournament.satelliteTarget,
    rawTournament.maxPlayers,
    rawTournament.tournGroup,
    rawTournament.tournFlags
  );
  const fallbackImageUrl = utils.mttUtils.defaultImage(
    rawTournament.gameInt,
    rawTournament.structureInt,
    rawTournament.buyIn,
    0,
    Number(rawTournament.isHiLo),
    0
  );

  return {
    ...rawTournament,
    isPlayMoney,
    tags,
    imageUrl,
    fallbackImageUrl,
    formattedName,
    formattedSatelliteTargetName,
    formattedSidecarName,
    formattedGame,
    formattedState,
    ...formattedAttribtues
  };
};

export const normalizeTournamentsData = (
  tournaments: TournamentRaw[],
  locale: string,
  timeZone: string
) => {
  const data: NormalizedTournamentsResponse = {
    tournamentsData: {},
    tournamentsCount: tournaments.length,
    tournamentsCurrency: 'MULTIPLE'
  };
  const currencyMap = new Map<Currency, boolean>();

  tournaments.forEach((tournament) => {
    data.tournamentsData[tournament.id] = formatTournament(tournament, locale, timeZone);
    currencyMap.set(tournament.currency, true);
  });

  const currencies = Array.from(currencyMap.keys());
  data.tournamentsCurrency = currencies.length === 1 ? currencies[0] : 'MULTIPLE';

  return data;
};

const defaultGetOrder = (tournament: Tournament): number =>
  TOURNAMENT_STATES_ORDER[tournament.formattedState] || TOURNAMENT_STATES_ORDER.default;

export const stateTimeComparator = (
  tournamentA: Tournament,
  tournamentB: Tournament,
  getOrder: (tournament: Tournament) => number = defaultGetOrder
) => {
  const firstRowOrder = getOrder(tournamentA);
  const secondRowOrder = getOrder(tournamentB);

  const stateCompare = firstRowOrder - secondRowOrder;

  if (stateCompare === 0) {
    return tournamentA.formattedState === TournamentState.LATE_REGISTRATION
      ? tournamentA.regEndInt - tournamentB.regEndInt
      : tournamentA.whenStartInt - tournamentB.whenStartInt;
  }

  return stateCompare;
};

export const rowStateTimeComparator = (
  rowTournamentA: Row<Tournament>,
  rowTournamentB: Row<Tournament>
) => stateTimeComparator(rowTournamentA.original, rowTournamentB.original);

export const mapQueryVariables = (
  {
    siteName,
    isEmbedded,
    isPlayMoney,
    country = undefined,
    upcomingOnly = false
  }: CommonQueryVariableOptions,
  cmsVariables?: PokerMttQueryVariables
): TournamentsQueryVariables => {
  const variables: Required<TournamentsQueryVariables> = {
    include: {
      sites: [SITE_NAME_SITE_VARIABLE_MAP[siteName]],
      flags2: !isEmbedded ? ['HTML5_ALLOWED'] : undefined,
      currencies: isPlayMoney ? ['PLAY'] : [],
      country
    },
    exclude: {
      currencies: isPlayMoney ? [] : ['PLAY'],
      flags1: ['DUMMY', 'SITANDGO']
    }
  };

  if (upcomingOnly && variables.include) {
    /*
    - PUBLISHED - contains Announced - needed for showing Announed
    - SEATING - contains Seating -  needed for showing Seating
    - RUNNING - contains Late Registration - needed for push updates as we hide when tourney goes in Running
    - CANCELLED - contains Cancelled - needed for push updates as we need in table to receive Delete pushes
    */
    variables.include.statuses = ['PUBLISHED', 'SEATING', 'RUNNING', 'CANCELLED'];
    variables.include.startsIn = 'H_24';
  }

  return merge(variables, normalizeConfigQueryVariables(cmsVariables));
};

/* @todo rename filterTournaments when HG are extracted from swf-poker */
export const filterMTTTournaments = (variables: FilterVariables, tournaments: Tournament[]) => {
  const { games, structures, total, speeds, seats, channels, upcomingOnly, state } = variables;

  return tournaments.filter((tournament) => {
    const conditions: boolean[] = [];

    if (upcomingOnly)
      conditions.push(
        !utils.mttUtils.isCancelled(tournament.legacyStatus) &&
          (!utils.mttUtils.isRunning(tournament.legacyStatus) ||
            utils.mttUtils.isRegOpen(tournament.legacyStatus))
      );
    if (games?.length)
      conditions.push(
        structures?.length
          ? games.includes(tournament.game) && structures.includes(tournament.structure)
          : games.includes(tournament.game)
      );
    if ((total?.minimum || total?.minimum === 0) && (total?.maximum || total?.maximum === 0)) {
      const buyInTotal = tournament.buyIn + tournament.fee;
      conditions.push(
        buyInTotal >= total.minimum && (total.maximum === 0 ? true : buyInTotal <= total.maximum)
      );
    }
    if (state?.length) conditions.push(state.includes(tournament.formattedState));
    if (speeds?.length) conditions.push(speeds.includes(tournament.tournSpeed));
    if (seats?.length) conditions.push(seats.includes(tournament.seats));
    if (channels?.length)
      conditions.push(channels.some((channel) => tournament.channels.includes(channel)));

    return !conditions.some((condition) => !condition);
  });
};
